import { FaCheckCircle } from "react-icons/fa";
import { useInView } from "../hooks/useInView";
import { useState } from "react";

const RepresentativeExample = () => {
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const breakdown = [
    { label: "Loan Amount", value: "₹10,000", highlight: false, icon: "💰" },
    { label: "Processing Fee (10%)", value: "₹1,000", highlight: false, icon: "📊" },
    { label: "GST on Processing Fee (18%)", value: "₹180", highlight: false, icon: "📈" },
    { label: "Total Deduction", value: "₹1,180 (11.8%)", highlight: true, icon: "➖" },
    { label: "Final Amount Disbursed", value: "₹8,820", highlight: true, icon: "✅" },
  ];

  return (
    <section ref={ref} className="w-full py-16 md:py-20 lg:py-24 bg-gradient-to-br from-[#A9F9EE] via-[#7DD4D0] to-[#21A6B5] relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-float"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-[#3D4A7C] rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/3 w-72 h-72 bg-[#21A6B5] rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Enhanced Header */}
        <div className={`text-center mb-10 md:mb-12 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="inline-block mb-4">
            <span className="px-4 py-1 bg-gradient-to-r from-[#21A6B5] to-[#3D4A7C] text-white text-base leading-6 font-semibold text-center font-['Fredoka'] rounded-full shadow-md">
              💡 Transparent Pricing
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-[60px] lg:leading-[60px] font-bold text-center text-[#323232] mb-4 font-['Inter'] drop-shadow-md">
            Representative Example
          </h2>
          <p className="text-lg md:text-xl lg:text-[24px] lg:leading-[32px] font-normal text-center text-[#2C3E50] font-['Inter']">
            How My Salary Loan Works - Complete Breakdown
          </p>
        </div>

        <div className={`bg-white rounded-3xl p-8 md:p-12 lg:p-16 shadow-2xl transition-all duration-700 ${
          isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`} style={{ transitionDelay: '200ms' }}>
          <div className="space-y-6 md:space-y-8">
            {breakdown.map((item, index) => (
              <div
                key={index}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className={`group relative flex justify-between items-center p-6 md:p-8 rounded-2xl transition-all duration-700 overflow-hidden ${
                  item.highlight
                    ? "bg-gradient-to-r from-[#3D4A7C] to-[#5DADE2] text-white shadow-2xl"
                    : "bg-gradient-to-r from-gray-50 to-gray-100 hover:from-[#E0F7FF] hover:to-[#B3E5FC]"
                } ${
                  isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                } ${
                  hoveredIndex === index ? 'scale-105 -translate-y-1' : 'scale-100'
                }`}
                style={{ transitionDelay: `${(index + 3) * 100}ms` }}
              >
                {/* Shine Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </div>

                {/* Icon */}
                <div className={`absolute left-4 text-3xl md:text-4xl transition-all duration-500 ${
                  hoveredIndex === index ? 'scale-125 rotate-12' : 'scale-100 rotate-0'
                }`}>
                  {item.icon}
                </div>

                {/* Content */}
                <div className="relative z-10 flex justify-between items-center w-full pl-12 md:pl-16 pr-12 md:pr-16">
                  <span className={`text-lg md:text-xl lg:text-2xl font-semibold font-['Inter'] transition-all duration-300 ${
                    hoveredIndex === index ? 'scale-105' : 'scale-100'
                  }`}>
                    {item.label}
                  </span>
                  <span className={`text-xl md:text-2xl lg:text-3xl font-bold font-['Inter'] transition-all duration-300 ${
                    hoveredIndex === index ? 'scale-110' : 'scale-100'
                  }`}>
                    {item.value}
                  </span>
                </div>

                {/* Check Icon for highlighted items */}
                {item.highlight && (
                  <div className={`absolute right-4 text-2xl md:text-3xl transition-all duration-500 ${
                    hoveredIndex === index ? 'scale-125 rotate-12' : 'scale-100 rotate-0'
                  }`}>
                    <FaCheckCircle />
                  </div>
                )}

                {/* Animated Border */}
                {!item.highlight && (
                  <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#5DADE2] to-[#3D4A7C] transition-all duration-500 ${
                    hoveredIndex === index ? 'w-full' : 'w-0'
                  }`}></div>
                )}
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className={`mt-10 p-6 md:p-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 rounded-2xl shadow-lg transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`} style={{ transitionDelay: '800ms' }}>
            <div className="flex items-start gap-4">
              <div className="text-3xl">⚠️</div>
              <div>
                <p className="text-base md:text-lg text-gray-700 font-['Inter'] leading-relaxed">
                  <strong className="text-[#3D4A7C]">Important Note:</strong> This is a representative example. Actual processing fees and GST may vary based on loan amount and terms. Please refer to the loan agreement for exact details.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className={`text-center mt-10 transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`} style={{ transitionDelay: '900ms' }}>
            <p className="text-lg md:text-xl text-gray-600 font-['Inter'] mb-6">
              Transparent pricing with no hidden charges!
            </p>
            {/* <a
              href={import.meta.env.VITE_WEBSITE_LINK}
              target="_blank"
              className="inline-block px-10 py-4 bg-gradient-to-r from-[#3D4A7C] to-[#5DADE2] text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 animate-pulse-glow"
            >
              Calculate Your Loan →
            </a> */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RepresentativeExample;
